"use client"

import { SidebarLinkType } from '../../constants/content';
import { cn, removeRegionFromPath } from '../../lib/utils';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React from 'react'
import { useSidebar } from '../ui/sidebar';
import { useRegionAwareHref } from '../../hooks/use-region-aware-href';
import { useRegion } from '../../hooks/use-region';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { PROFILE_NAV_LOCK_TOOLTIP } from '@/features/profile/hooks/use-nav-profile-lock';
import ProfileNavLockDialog from './profile-nav-lock-dialog';



function SidebarLink({ icon, name, url, isRegionAware, navLocked = false }: SidebarLinkType & { navLocked?: boolean }) {

    const pathname = usePathname()
    const { regionSlug } = useRegion()
    const regionHref = useRegionAwareHref(url)
    const href = isRegionAware ? regionHref : url

    const normalizedUrl = url.startsWith('/') ? url : `/${url}`
    const basePath = React.useMemo(() => {
        if (isRegionAware && regionSlug) {
            return removeRegionFromPath(pathname)
        }
        return pathname
    }, [isRegionAware, regionSlug, pathname])

    const isActive = React.useMemo(() => {
        if (!basePath) return false

        if (basePath === normalizedUrl) return true
        return basePath.startsWith(`${normalizedUrl}/`)
    }, [basePath, normalizedUrl])
    const { toggleSidebar } = useSidebar()
    const [profileLockDialogOpen, setProfileLockDialogOpen] = React.useState(false)

    const onClick = () => {
        const width = window.innerWidth
        if(width < 768) {
            toggleSidebar()
        }        
    }

    const linkClassName = cn(
        `flex items-center gap-3 px-3 py-1.5 rounded-md bg-transparent text-base text-muted-foreground
        cursor-pointer border border-transparent relative
        hover:text-foreground active:scale-95 transition-all ring-primary outline-primary`,
        {
            "bg-primary/10 text-foreground": isActive,
        }
    )

    if (navLocked) {
        return (
            <>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            type="button"
                            className={cn(linkClassName, "w-full cursor-not-allowed opacity-50")}
                            aria-disabled="true"
                            title={name}
                            onClick={() => setProfileLockDialogOpen(true)}
                        >
                            <Image 
                                src={icon}
                                alt={`${name}-icons`}
                                className='w-6'
                            />
                            <span className={`text-md block`}>
                                {name}
                            </span>
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">{PROFILE_NAV_LOCK_TOOLTIP}</TooltipContent>
                </Tooltip>
                <ProfileNavLockDialog
                    open={profileLockDialogOpen}
                    onOpenChange={setProfileLockDialogOpen}
                />
            </>
        )
    }

    return (
        <Link
            href={href}
            className={linkClassName}
            onClick={onClick}
            title={name}
        >
            <Image 
                src={icon}
                alt={`${name}-icons`}
                className='w-6'
            />
            <span className={`text-md block`}>
                {name}
            </span>
        </Link>
    );
}




export default SidebarLink